import { memo, type ReactNode, useMemo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { CloseSelectItem } from "./CloseSelectItem";

interface CloseSelectItem {
    id: string;
    entityId?: number;
    name: string;
    startDate?: string | null;
    endDate?: string | null;
}

interface CloseSelectProps {
    value: string | null;
    items: CloseSelectItem[];
    nullLabel: string;
    onValueChange: (value: string | null) => void;
    /** A Picker trigger part (e.g. `Picker.CellContent`). */
    trigger: ReactNode;
}

/**
 * The close or close stage picker behind CloseSelectCell, opened from
 * `trigger`. Picking the current value changes nothing.
 */
export const CloseSelect = ({
    value,
    items,
    nullLabel,
    onValueChange,
    trigger,
}: CloseSelectProps) => {
    const changeValue = (nextValue: string | null) => {
        if (nextValue !== value) {
            onValueChange(nextValue);
        }
    };
    return (
        <Picker.Select value={value} onValueChange={changeValue}>
            {trigger}
            <Picker.Popup>
                <Picker.List>
                    <Picker.Item onSelect={() => changeValue(null)}>
                        {nullLabel}
                    </Picker.Item>
                    {items.map((item) => (
                        <CloseSelectItem
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            entityId={item.entityId}
                            startDate={item.startDate}
                            endDate={item.endDate}
                        />
                    ))}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};

/**
 * Shared inline-editable select cell used by CloseStageCell and CloseCell.
 * Renders a Picker.Select with a Picker.CellContent trigger.
 */
export const CloseSelectCell = memo(
    ({
        value,
        items,
        addText,
        nullLabel,
        onValueChange,
    }: Omit<CloseSelectProps, "trigger"> & { addText: string }) => {
        const selectedName = useMemo(
            () => items.find((item) => item.id === value)?.name,
            [value, items],
        );

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText={addText}
                    hideAddIcon
                    value={value ? (selectedName ?? "") : undefined}
                />
            ),
            [value, selectedName, addText],
        );

        return (
            <ViewTableCell>
                <CloseSelect
                    value={value}
                    items={items}
                    nullLabel={nullLabel}
                    onValueChange={onValueChange}
                    trigger={trigger}
                />
            </ViewTableCell>
        );
    },
);
